// Emergency Numbers → start a scan. Read-only by construction: a scan never
// changes anything unless auto-fix is switched on in Settings AND asked for here.
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Play } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { PLATFORM_LABEL } from "./ui";

const SCOPES = [
  ["siptrunk", "SIP trunk domains", "the ~400 domains with a customer trunk — where Static vs DLR actually matters"],
  ["all", "Every domain", "trunks first, then the rest; takes a while on 4,000+ domains"],
  ["domains", "Specific domains", "one per line"],
];

export default function ScanLauncher({ platforms, conns, canEdit, running, onStarted }) {
  const [scope, setScope] = useState("siptrunk");
  const [domains, setDomains] = useState("");
  const [picked, setPicked] = useState(platforms);
  const [buildNumbers, setBuildNumbers] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    const list = domains.split(/[\s,;]+/).map((d) => d.trim().toLowerCase()).filter(Boolean);
    if (scope === "domains" && !list.length) { toast.error("Name at least one domain"); return; }
    if (!picked.length) { toast.error("Pick at least one platform"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/e911/scan", { scope, platforms: picked, domains: list, build_numbers: buildNumbers });
      toast.success(`Scan ${data.run?.run_id} started`);
      onStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the scan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-4 px-4 py-4" data-testid="e911-scan-launcher">
      <div>
        <div className="text-sm font-semibold text-slate-800">Run a scan</div>
        <p className="text-[12px] text-slate-500">
          Reads every domain's 911 setup from the Portal and Bandwidth and writes a plain-English verdict. Nothing is changed.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-3" data-testid="e911-scan-scopes">
        {SCOPES.map(([v, label, hint]) => (
          <button key={v} type="button" onClick={() => setScope(v)} disabled={!canEdit} data-testid={`e911-scan-scope-${v}`}
                  className={`rounded-md border px-3 py-2 text-left transition-colors ${
                    scope === v ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-slate-200 hover:bg-slate-50"} disabled:opacity-60`}>
            <div className="text-sm font-medium text-slate-800">{label}</div>
            <div className="text-[11px] text-slate-500">{hint}</div>
          </button>
        ))}
      </div>

      {scope === "domains" && (
        <Textarea rows={3} value={domains} onChange={(e) => setDomains(e.target.value)} disabled={!canEdit}
                  placeholder={"acme.ca\nexample.trunk"} className="font-mono text-[12px]" data-testid="e911-scan-domains" />
      )}

      <div className="flex flex-wrap items-center gap-4 text-sm">
        {platforms.length > 1 && (
          <div className="flex items-center gap-3" data-testid="e911-scan-platforms">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Platforms</span>
            {platforms.map((p) => {
              const ok = conns?.[`uc_platform_${p}`]?.configured;
              return (
                <label key={p} className={`flex items-center gap-1.5 ${ok ? "text-slate-700" : "text-slate-400"}`}
                       title={ok ? "" : "not connected"}>
                  <input type="checkbox" className="h-4 w-4" checked={picked.includes(p)} disabled={!canEdit || !ok}
                         data-testid={`e911-scan-platform-${p}`}
                         onChange={(e) => setPicked((cur) => (e.target.checked ? [...cur, p] : cur.filter((x) => x !== p)))} />
                  {PLATFORM_LABEL[p] || p}
                </label>
              );
            })}
          </div>
        )}
        <label className="flex items-center gap-1.5 text-slate-700" data-testid="e911-scan-build-numbers">
          <input type="checkbox" className="h-4 w-4" checked={buildNumbers} disabled={!canEdit}
                 onChange={(e) => setBuildNumbers(e.target.checked)} />
          Rebuild the numbers report afterwards
        </label>
        <div className="ml-auto">
          <Button size="sm" onClick={start} disabled={!canEdit || busy || running} data-testid="e911-scan-start">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {running ? "A run is in progress" : "Start scan"}
          </Button>
        </div>
      </div>
      {!canEdit && <p className="text-[11px] text-slate-400">You can view the results but not start a scan (needs e911.edit).</p>}
    </Card>
  );
}
